import { useState } from "react";
import axios from "axios";
import { Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { useLang, WHATSAPP_LINK, HAS_WHATSAPP } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.contact.sent);
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-pad bg-cb-accent-surface">
      <div className="container-cb grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="overline mb-4 flex items-center gap-3"><span className="gold-divider" /> {t.contact.overline}</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-cb-primary">{t.contact.title}</h2>
          <p className="mt-4 text-cb-secondary text-base lg:text-lg">{t.contact.sub}</p>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3" data-testid="contact-address">
              <div className="w-10 h-10 rounded-lg bg-white grid place-items-center text-cb-emerald flex-shrink-0"><MapPin size={18} /></div>
              <div className="text-sm text-cb-primary leading-relaxed pt-2">{t.contact.address}</div>
            </div>
            <a href={`mailto:${t.contact.email_value}?subject=${encodeURIComponent("Financial Management Heath Check")}`} className="flex items-start gap-3 group" data-testid="contact-email">
              <div className="w-10 h-10 rounded-lg bg-white grid place-items-center text-cb-emerald flex-shrink-0"><Mail size={18} /></div>
              <div className="pt-2">
                <div className="text-xs uppercase tracking-widest text-cb-secondary">{t.contact.email_label}</div>
                <div className="text-cb-primary group-hover:text-cb-emerald transition-colors">{t.contact.email_value}</div>
              </div>
            </a>
            {HAS_WHATSAPP && (
              <a href={WHATSAPP_LINK("Hi Capital Bay, I'd like to talk.")} target="_blank" rel="noreferrer" className="flex items-start gap-3 group" data-testid="contact-whatsapp">
                <div className="w-10 h-10 rounded-lg bg-cb-emerald grid place-items-center text-white flex-shrink-0"><MessageCircle size={18} /></div>
                <div className="pt-2">
                  <div className="text-xs uppercase tracking-widest text-cb-secondary">{t.contact.phone_label}</div>
                  <div className="text-cb-primary group-hover:text-cb-emerald transition-colors" dir="ltr">{t.contact.phone_value}</div>
                </div>
              </a>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 card-cb p-7 lg:p-9" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-cb-secondary">{t.contact.name}</label>
              <Input value={form.name} onChange={upd("name")} required data-testid="contact-name" className="mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-cb-secondary">{t.contact.email}</label>
              <Input type="email" value={form.email} onChange={upd("email")} required data-testid="contact-email-input" className="mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-cb-secondary">{t.contact.phone}</label>
              <Input value={form.phone} onChange={upd("phone")} data-testid="contact-phone-input" className="mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-cb-secondary">{t.contact.company}</label>
              <Input value={form.company} onChange={upd("company")} data-testid="contact-company-input" className="mt-1" />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-cb-secondary">{t.contact.message}</label>
            <Textarea value={form.message} onChange={upd("message")} rows={5} required data-testid="contact-message" className="mt-1" />
          </div>
          <button type="submit" disabled={loading} className="mt-6 btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium disabled:opacity-50" data-testid="contact-submit">
            <Send size={16} />
            {loading ? "..." : t.contact.send}
          </button>
        </form>
      </div>
    </section>
  );
}
